import { useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useSEO } from '@/hooks/useSEO';
import { User, ArrowLeft, Calendar } from 'lucide-react';

type AuthorArticle = {
  title: string;
  slug: string;
  lead?: string | null;
  createdAt?: string | null;
  category?: string | null;
};

type AuthorPayload = {
  author: {
    id: string;
    name: string;
    slug: string;
    photoUrl?: string | null;
    bio?: string | null;
    role?: string | null;
  };
  articles: AuthorArticle[];
};

function useAuthorSlugFromPath(): string {
  const [location] = useLocation();
  const parts = location.split('/').filter(Boolean);
  // /autor/:slug
  return parts[0] === 'autor' ? decodeURIComponent(parts[1] || '') : '';
}

export default function AuthorProfile() {
  const slug = useAuthorSlugFromPath();

  const { data, isLoading, error } = useQuery<AuthorPayload>({
    queryKey: ['/api/authors/slug', slug],
    queryFn: async () => {
      const res = await fetch(`/api/authors/slug/${encodeURIComponent(slug)}`);
      if (!res.ok) throw new Error('Author not found');
      return res.json();
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  const author = data?.author;
  const articles = data?.articles || [];
  const description =
    author?.bio?.slice(0, 160) ||
    (author ? `Perfil editorial de ${author.name} en Statemia.` : 'Autor en Statemia');

  useSEO({
    title: author ? `${author.name} | Statemia` : 'Autor | Statemia',
    description,
    type: 'website',
    canonicalUrl: slug ? `/autor/${slug}` : undefined,
    ogImage: author?.photoUrl || undefined,
    ogImageAlt: author?.name,
    author: author?.name,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">
        Autor no encontrado.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">
        Cargando perfil…
      </div>
    );
  }

  if (error || !author) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 space-y-4 text-center">
        <p className="text-muted-foreground">No encontramos este perfil de autor.</p>
        <Link href="/sobre-statemia" className="text-primary hover:underline">
          Sobre Statemia
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft size={16} />
          Volver al inicio
        </Link>

        <header className="flex flex-col sm:flex-row gap-6 sm:items-start mb-10 pb-8 border-b border-border">
          {author.photoUrl ? (
            <img
              src={author.photoUrl}
              alt={author.name}
              className="w-28 h-28 rounded-full object-cover flex-shrink-0"
              width={112}
              height={112}
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <User size={48} className="text-white" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-foreground tracking-tight">{author.name}</h1>
            {author.role ? (
              <p className="mt-1 text-lg text-muted-foreground">{author.role}</p>
            ) : null}
            {author.bio ? (
              <p className="mt-4 text-foreground/90 leading-relaxed whitespace-pre-line">
                {author.bio}
              </p>
            ) : null}
          </div>
        </header>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Artículos{articles.length > 0 ? ` (${articles.length})` : ''}
          </h2>
          {articles.length === 0 ? (
            <p className="text-muted-foreground">Aún no hay artículos publicados de este autor.</p>
          ) : (
            <ul className="space-y-5">
              {articles.map((article) => (
                <li key={article.slug} className="border-b border-border pb-5 last:border-0">
                  <Link
                    href={`/statistics/${article.slug}`}
                    className="group block hover:opacity-90"
                  >
                    <h3 className="text-lg font-medium text-foreground group-hover:underline">
                      {article.title}
                    </h3>
                    {article.lead ? (
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                        {article.lead}
                      </p>
                    ) : null}
                    {article.createdAt ? (
                      <p className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(article.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
